import gettext from '@eb/gettext';
import isUndefined from 'lodash/isUndefined';
import { joinValidators, makeValidator } from '@eb/eds-utils';
import {
    isProvided,
    makeHasMinLength,
    makeHasMaxLength,
    hasContent,
    isAnEmail,
} from '@eb/validators';
import { EMAILS_MISMATCHES } from '../constants/messages';

const EMAIL_VALIDATORS = [
    {
        validator: isProvided,
        errorMessage: gettext('Please enter a valid email address'),
    },
    {
        validator: isAnEmail,
        errorMessage: gettext('Please enter a valid email address'),
    },
];

const PASSWORD_LOGIN_VALIDATOR = [
    {
        validator: isProvided,
        errorMessage: gettext('Password is required'),
    },
];

const PASSWORD_SIGNUP_VALIDATOR = [
    ...PASSWORD_LOGIN_VALIDATOR,
    {
        validator: makeHasMinLength(8),
        errorMessage: gettext('Password must be at least 8 characters'),
    },
];

const FIRST_NAME_VALIDATOR = [
    {
        validator: isProvided,
        errorMessage: gettext('First name is required'),
    },
    {
        validator: hasContent,
        errorMessage: gettext('First name cannot be spaces'),
    },
    {
        validator: makeHasMaxLength(50),
        errorMessage: gettext('First name must be less than 50 characters'),
    },
];

const LAST_NAME_VALIDATOR = [
    {
        validator: isProvided,
        errorMessage: gettext('Last name is required'),
    },
    {
        validator: hasContent,
        errorMessage: gettext('Last name cannot be spaces'),
    },
    {
        validator: makeHasMaxLength(50),
        errorMessage: gettext('Last name must be less than 50 characters'),
    },
];

const userInfoValidate = makeValidator({
    firstName: FIRST_NAME_VALIDATOR,
    lastName: LAST_NAME_VALIDATOR,
});

// 'makeValidator' method only supports validations in current input's value. Creating a validation
// this way we can access both values, current email and confirmEmail.
const confirmEmailValidate = function (
    { confirmEmail = '', email: formEmail = '' },
    { email = '' },
) {
    // The new flow gets the email from the form, the old flow from the store.
    const _email = formEmail || email;
    // We want to show this error starting from the moment the user writes a wrong character
    // but we can't validate if email is complete here (we use async for that)
    if (
        !isProvided(confirmEmail) ||
        !_email.toLowerCase().startsWith(confirmEmail.toLowerCase())
    ) {
        return {
            confirmEmail: EMAILS_MISMATCHES,
        };
    }
    return true;
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// We use async validate to make sure emails matches after leaving the field. This async validate runs onBlur but redux
// expects a promise so we have to emulate a timeout (as like a backend validation request) to make it work
export const submitAsyncValidateConfirmEmail = function (
    { confirmEmail = '', email: formEmail = '' },
    dispatch,
    { email = '' },
    fieldString,
) {
    // The new flow gets the email from the form, the old flow from the store.
    const _email = formEmail || email;
    return sleep(10).then(() => {
        if (
            isUndefined(fieldString) &&
            confirmEmail.toLowerCase() !== _email.toLowerCase()
        ) {
            const error = {
                confirmEmail: EMAILS_MISMATCHES,
            };

            throw error;
        }
    });
};

export const asyncValidateConfirmEmail = function (
    { confirmEmail = '', email: formEmail = '' },
    dispatch,
    { email = '' },
) {
    // The new flow gets the email from the form, the old flow from the store.
    const _email = formEmail || email;

    return sleep(10).then(() => {
        if (confirmEmail.toLowerCase() !== _email.toLowerCase()) {
            const error = { confirmEmail: EMAILS_MISMATCHES };

            throw error;
        }
    });
};

export const emailValidate = makeValidator({
    email: EMAIL_VALIDATORS,
});

export const passwordValidate = makeValidator({
    password: PASSWORD_SIGNUP_VALIDATOR,
});

export const lookUpValidate = makeValidator({
    email: EMAIL_VALIDATORS,
});

export const logInValidate = makeValidator({
    password: PASSWORD_LOGIN_VALIDATOR,
});

export const newLoginValidate = makeValidator({
    email: EMAIL_VALIDATORS,
    password: PASSWORD_LOGIN_VALIDATOR,
});

export const signUpValidate = joinValidators(
    confirmEmailValidate,
    userInfoValidate,
    passwordValidate,
);

export const newSignupValidate = joinValidators(
    emailValidate,
    confirmEmailValidate,
    userInfoValidate,
    passwordValidate,
);

export const SocialSignUpValidate = joinValidators(
    lookUpValidate,
    userInfoValidate,
    passwordValidate,
);
